import React, {
  useContext,
  useState,
  useMemo,
  useReducer,
  useEffect,
} from "react";
import { type Demographics } from "@local-logic/client";
import { useTracking } from "@local-logic/core/tracking";
import { theme } from "@local-logic/core/ui";
import { ValidationError } from "yup";
import errorTracker from "~/utils/errorTracker";
import { useLocation } from "~/hooks/useLocation";
import useEffectOnce from "~/hooks/useEffectOnce";
import { useStateManagerContext } from "~/context/stateManager";
import {
  type DemographicsCategory,
  type LocalDemographicsProps,
  TrackingPipelineEvents,
  AnalyticsEvents,
} from "./types";
import * as DemographicsTabs from "./components/Content/DemographicsTabs";
import { validateOptions } from "./schema";
import { AVAILABLE_TABS } from "./constants";

export interface State {
  education_level?: Demographics["education_level"];
  employment_status?: Demographics["employment_status"];
  commute_mode?: Demographics["commute_mode"];
  household_composition?: Demographics["household_composition"];
  income?: Demographics["income"];
  income_stats?: Demographics["income_stats"];
  income_level?: Demographics["income_level"];
  population_age?: Demographics["population_age"];
  population_total?: Demographics["population_total"];
  housing_tenancy?: Demographics["housing_tenancy"];
  housing_type?: Demographics["housing_type"];
  property_tenure?: Demographics["property_tenure"];
  mother_tongue?: Demographics["mother_tongue"];
  official_language_knowledge?: Demographics["official_language_knowledge"];
  location: {
    lat: number;
    lng: number;
  };
  country: string;
}

type Action = Partial<State>;

export type DefaultContext = {
  demographicsState: State;
  setDemographicsState: React.Dispatch<Action>;
  activeCategory: DemographicsCategory;
  setActiveCategory: (value: DemographicsCategory) => void;
};

const DemographicsContext = React.createContext<DefaultContext>(
  {} as DefaultContext
);
const useDemographics = () => useContext(DemographicsContext);

const LocalDemographicsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const tracking = useTracking();
  const { sdkState } = useStateManagerContext<LocalDemographicsProps>();
  const [activeCategory, setActiveCategory] = useState(
    sdkState.options?.defaultTab &&
      AVAILABLE_TABS.includes(sdkState.options?.defaultTab)
      ? sdkState.options?.defaultTab
      : AVAILABLE_TABS[0]
  );
  const [validated, setValidated] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const {
    data: { country },
  } = useLocation({
    lat: sdkState.options?.lat,
    lng: sdkState.options?.lng,
  });

  useEffectOnce(() => {
    tracking.postEvent({
      eventType: TrackingPipelineEvents.SDK_VIEWED,
    });

    tracking.trackAnalyticsEvent({
      eventName: AnalyticsEvents.SDK_VIEWED,
    });
  });

  const [demographicsState, setDemographicsState] = useReducer(
    (state: State, action: Action) => ({
      ...state,
      ...action,
    }),
    {
      location: {
        lat: sdkState.options?.lat || 43.661337,
        lng: sdkState.options?.lng || -79.401903,
      },
      country: "CA",
    }
  );

  useEffect(() => {
    if (!sdkState) {
      return;
    }

    setDemographicsState({
      ...demographicsState,
      country,
    });
  }, [sdkState, country]);

  useEffectOnce(() => {
    const validate = async () => {
      try {
        errorTracker.addBreadCrumb(
          "LocalDemographicsProvider",
          "Validating state options",
          {
            options: sdkState.options!,
          }
        );

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        await validateOptions(sdkState.options!);
        setValidated(true);
      } catch (error) {
        const validationError = error as ValidationError;
        setValidationErrors(validationError.errors);

        errorTracker.addBreadCrumb(
          "LocalDemographicsProvider",
          "State option validation failed",
          {
            errors: validationError.errors,
          }
        );
      }
    };

    if (!validated) {
      validate();
    }
  });

  const contextValue = useMemo(
    () => ({
      demographicsState,
      setDemographicsState,
      activeCategory,
      setActiveCategory,
    }),
    [activeCategory, setActiveCategory, demographicsState, setDemographicsState]
  );

  const handleTabsChange = (value: {
    type: "tabs";
    data: DemographicsCategory;
  }) => {
    setActiveCategory(value.data);
    errorTracker.addBreadCrumb(
      "LocalDemographicsProvider",
      "handleTabsChange",
      {
        value,
      }
    );
    tracking.postEvent({
      eventType: TrackingPipelineEvents.TAB_CLICKED,
      eventAttributes: [
        {
          label: "activeCategory",
          data: value.data,
        },
      ],
    });

    tracking.trackAnalyticsEvent({
      eventName: AnalyticsEvents.CATEGORY_SELECTED,
      eventAttributes: {
        ll_selected_category: value.data,
      },
    });
  };

  const omitTabs = useMemo(() => {
    const tabsToOmit: string[] = [];
    const dataKeys = {
      income: "income",
      population: "population_age",
      housing: "housing_tenancy",
      household: "household_composition",
      education: "education_level",
      commute: "commute_mode",
      languages: "mother_tongue",
    };

    Object.entries(dataKeys).forEach(([key, value]) => {
      if (!Object.keys(demographicsState).includes(value)) {
        tabsToOmit.push(key);
      }
    });

    errorTracker.addBreadCrumb(
      "LocalDemographicsProvider",
      "Updated tabs to omit",
      {
        tabsToOmit,
      }
    );

    return tabsToOmit;
  }, [demographicsState]);

  if (!validated) {
    if (validationErrors.length) {
      const error = "Could not load Demographics";
      // eslint-disable-next-line no-console
      console.error(`${error}: ${validationErrors.join(", ")}`);
      return <p>{error}</p>;
    }

    return <></>;
  }

  return (
    <DemographicsContext.Provider value={contextValue}>
      <DemographicsTabs.Root
        options={{ activeTab: activeCategory, omitTabs }}
        onChange={handleTabsChange}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            background: `${theme["--ll-color-background"]}`,
            padding: `${theme["--ll-spacing-xx-small"]} ${theme["--ll-spacing-small"]} 0`,
            borderRadius: `${theme["--ll-border-radius-medium"]}`,
          }}
        >
          {children}
        </div>
      </DemographicsTabs.Root>
    </DemographicsContext.Provider>
  );
};

export { useDemographics, LocalDemographicsProvider };
